import React from 'react';
import { useTranslation } from 'react-i18next';
import { viewportSafeAreaInsetBottom } from '@telegram-apps/sdk-react';

import { listStyle } from './styles.ts';

import { Contact, MessageUI } from '@/api/messaging/types.ts';
import { MessageItem } from '@/components/Messanger/Message/MessageItem/MessageItem.tsx';
import { Gender, MessageLabels, MessageTypes } from '@/enums/apiEnums.ts';
import {
  matchMessageStyle,
  baitMessageStyle,
  specialEventSystemMessage
} from '@/components/Messanger/Message/MessageItem/styles.ts';
import HeartArrowShape from '@/components/common/svg/shapes/HeartArrowShape.tsx';
import { useProfileStore } from '@/store/useProfileStore.ts';
import CrownShape from '@/components/common/svg/shapes/CrownShape.tsx';
import DateSeparator from '@/components/Messanger/Message/DateSeparator/DateSeparator.tsx';

interface MessageListProps {
  messages: MessageUI[];
  fetchedLength: number;
  lastMessageRef?:
    | React.RefObject<HTMLDivElement>
    | ((node: HTMLDivElement | null) => void);
  contact: Contact | null;
  onResend?: (messageId: string) => void;
  onReply?: (message: MessageUI) => void;
  highlightedMessageId?: string | null;
  onReplyMessageClick?: (messageId: string) => void;
  contentRef?: React.RefObject<HTMLDivElement>;
}

const MessageList: React.FC<MessageListProps> = ({
  messages,
  fetchedLength,
  lastMessageRef,
  contact,
  onResend,
  onReply,
  highlightedMessageId,
  onReplyMessageClick,
  contentRef
}) => {
  const { t } = useTranslation();
  const { aboutMe } = useProfileStore();

  const safeAreaBottom = viewportSafeAreaInsetBottom();

  const processedMessages = messages.reduce(
    (messagesAcc: MessageUI[], message) => {
      messagesAcc.push(message);

      if (
        message.type === MessageTypes.TYPE_ICEBREAKER_GIFT &&
        message.icebreaker_gift?.message
      ) {
        const textMessage: MessageUI = {
          ...message,
          type: MessageTypes.TYPE_TEXT,
          text_body: {
            message: message.icebreaker_gift.message
          }
        };
        messagesAcc[messages.length] = message;
        messagesAcc[messages.length - 1] = textMessage;
      }

      return messagesAcc;
    },
    []
  );

  // Check if any message is a gift with an attached message
  const messagesHaveGift = messages.some(
    (msg) =>
      msg.type === MessageTypes.TYPE_ICEBREAKER_GIFT &&
      !!msg.icebreaker_gift?.message
  );

  // Calculate fetchedLength accounting for gift messages
  const fetchedLengthAcc = messagesHaveGift ? fetchedLength + 1 : fetchedLength;

  // Helper to get date from create_micros (microseconds timestamp)
  const getDateFromMicros = (micros: string): Date => {
    return new Date(Number(micros) / 1000);
  };

  // Helper to check if two dates are on the same day
  const isSameDay = (date1: Date, date2: Date): boolean => {
    return (
      date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate()
    );
  };

  // Group messages by date and prepare render elements
  const elements: JSX.Element[] = [];
  let messageIndex = 0;

  // Don't show separators if there's only 1 message
  const showDateSeparators = processedMessages.length > 1;

  processedMessages.forEach((message, index) => {
    const lastIndex = processedMessages.length - (fetchedLengthAcc - 1);
    const isLastMessageRef = messageIndex === lastIndex;

    const currentMessageDate = getDateFromMicros(message.create_micros);

    // First, push the message
    elements.push(
      <div
        key={`${message.create_micros}-${index}`}
        ref={isLastMessageRef ? lastMessageRef : null}
        style={{ marginTop: '5px' }}
        data-message-id={message.create_micros}
      >
        <MessageItem
          message={message}
          contactName={contact?.name}
          onResend={onResend}
          onReply={onReply}
          isHighlighted={highlightedMessageId === message.create_micros}
          onReplyMessageClick={onReplyMessageClick}
          contentRef={contentRef}
        />
      </div>
    );

    const giftMessage = message.icebreaker_gift;
    const baitMessage = message.bait_body;

    if (giftMessage && message.type === MessageTypes.TYPE_ICEBREAKER_GIFT) {
      const isInbox = message.labels.includes(MessageLabels.LABEL_INBOX);
      const gender = isInbox ? contact?.gender : aboutMe?.profile.gender;
      const translationKey = `chats.${isInbox ? 'new_gift' : 'sent_gift'}.${gender === Gender.MALE ? 'm' : 'f'}`;

      elements.push(
        <div style={matchMessageStyle}>
          <div style={specialEventSystemMessage}>
            <HeartArrowShape
              width={20}
              height={20}
              fillColor="#bc4141"
              style={{
                flexShrink: 0,
                marginRight: 4,
                display: 'block'
              }}
            />
            <div style={{ color: '#ffffff' }}>
              {(isInbox ? contact : aboutMe) && gender && t(translationKey)}
            </div>
          </div>
        </div>
      );
    }

    if (baitMessage && message.type === MessageTypes.TYPE_BAIT) {
      elements.push(
        <div style={matchMessageStyle}>
          <div style={baitMessageStyle}>
            <CrownShape
              width={18}
              height={18}
              fillColor="#FFF"
              style={{
                flexShrink: 0,
                marginRight: 6,
                display: 'block'
              }}
            />
            <div style={{ color: '#ffffff', fontSize: 12, fontWeight: 500 }}>
              {t('message.vip')}
            </div>
          </div>
        </div>
      );
    }

    // Add date separator AFTER message (appears ABOVE in column-reverse)
    // Show separator if this is the "first message of the day" (visually topmost message of that day)
    if (showDateSeparators) {
      const nextMessage = processedMessages[index + 1];

      if (nextMessage) {
        const nextMessageDate = getDateFromMicros(nextMessage.create_micros);
        if (!isSameDay(currentMessageDate, nextMessageDate)) {
          // Show separator with current message's date (it's the newest message of its day group)
          elements.push(
            <DateSeparator
              key={`separator-${message.create_micros}`}
              date={currentMessageDate}
            />
          );
        }
      } else {
        // This is the oldest message - add separator above it
        elements.push(
          <DateSeparator
            key={`separator-${message.create_micros}`}
            date={currentMessageDate}
          />
        );
      }
    }

    messageIndex++;
  });

  return (
    <div style={{ ...listStyle, paddingBottom: safeAreaBottom + 10 }}>
      {elements}
    </div>
  );
};

export default MessageList;
